import {
  Button,
  Input,
  Tag,
  toastEmitter,
} from "@anchor/react-components/dist/lib/components";
import React, { useEffect, useState } from "react";
import { getAPI, postAPI } from "../../../Services/APIservices";
import "../SenerioRequest.scss";
import { filter } from "lodash";
import { useForm, Controller } from "react-hook-form";
import { appRoles } from "../../../utils/Constants";

interface GeneratorProps {
  handleGeneratedData?: (data: CardItem[]) => void;
  cardData?: CardItem[];
  onEditClick?: (card: CardItem) => void; // <-- NEW PROP
}

export interface Condition {
  conditionId: number;
  cargoFlowDirectionId: number;
  period: number;
  minPercentage: number;
  maxPercentage: number;
  cargoFlowDirectionName: string;
}

export interface CardItem {
  scenarioId: number;
  scenarioName: string;
  conditions?: Condition[];
  isActive: boolean;
}

const CardList = ({
  handleGeneratedData,
  cardData = [],
  onEditClick,
}: GeneratorProps) => {
  const { control } = useForm();

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [cardItems, setCardItems] = useState<CardItem[]>(cardData);
  const [loading, setLoading] = useState<boolean>(true);
  const [filteredItems, setFilteredItems] = useState<CardItem[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [targetCardId, setTargetCardId] = useState<number | null>(null);
  const userRole = sessionStorage.getItem("userRole");

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 7; // Customize as needed

  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  const transformApiResponse = (flatData: any[]): CardItem[] => {
    if (!Array.isArray(flatData)) {
      throw new Error("Invalid input: 'card Data' must be an array.");
    }

    const result: CardItem[] = [];
    const scenarioMap: { [key: string]: CardItem } = {};

    flatData.forEach((item) => {
      const {
        scenarioId,
        scenarioName,
        isActive,
        conditionId,
        cargoFlowDirectionId,
        period,
        minPercentage,
        maxPercentage,
        cargoFlowDirectionName,
      } = item;

      const scenarioKey = String(scenarioId);

      if (!scenarioMap[scenarioKey]) {
        scenarioMap[scenarioKey] = {
          scenarioId: scenarioId,
          scenarioName: scenarioName,
          isActive: isActive ?? false,
          conditions: [],
        };
        result.push(scenarioMap[scenarioKey]);
      }

      scenarioMap[scenarioKey]?.conditions?.push({
        conditionId: conditionId,
        cargoFlowDirectionId,
        period,
        minPercentage,
        maxPercentage,
        cargoFlowDirectionName,
      });
    });

    return result.sort((a, b) => Number(b.isActive) - Number(a.isActive));
  };

  const fetchCardData = async () => {
    console.log("fething card data");
    try {
      const response = transformApiResponse(
        await getAPI(
          `simulation-api/simulation/GetAllModuleTriggerScenariosAsync`,
        ),
      );
      setCardItems(response);
      setFilteredItems(response);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching card items:", error);
      setLoading(false); // <--- handle error case too
    }
  };

  const toggleCard = (id: number) => {
    setExpandedId((prevId) => (prevId === id ? null : id));
  };

  const handleSearch = (searchText: string) => {
    if (searchText.trim() === "") {
      setFilteredItems(cardItems);
    } else {
      const filtered = cardItems.filter((item) =>
        item.scenarioName.toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredItems(filtered);
    }
  };

  const updateScenerioStatus = async (id: number) => {
    const item = cardItems.find((c) => c.scenarioId === id);
    if (!item) return;

    const newStatus = !item.isActive;

    const payload = {
      id: item.scenarioId,
      name: item.scenarioName,
      isActive: newStatus,
    };

    try {
      // Call API to update status
      const response =   await postAPI(
        `simulation-api/simulation/SaveModuleTriggerScenariosAsync`,
        payload,
      );

      // On success, update UI
      const updatedItems = cardItems.map((card) =>
        card.scenarioId === id ? { ...card, isActive: newStatus } : card,
      );


      

      const sortedItems = updatedItems.sort(
        (a, b) => Number(b.isActive) - Number(a.isActive),
      );
      setCardItems(sortedItems);
      setFilteredItems(sortedItems);
      handleGeneratedData?.(sortedItems);

      if (response > 0) {
        const successMessage = `Scenario ${item.scenarioName} ${newStatus ? "activated" : "deactivated"} successfully`
        toastEmitter(
          { className: "customClassName", title: successMessage },
          { type: "success" }
        );
      }

    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  useEffect(() => {
    fetchCardData();

    if (cardData.length > 0 && expandedId === null) {
      const sortedData = [...cardData].sort(
        (a, b) => Number(b.isActive) - Number(a.isActive),
      );
      setCardItems(sortedData);
      setFilteredItems(sortedData);
      const newlyActiveCard = sortedData.find((item) => item.isActive);
      if (newlyActiveCard) {
        setExpandedId(newlyActiveCard.scenarioId);
      }
    } else {
      const sortedData = [...cardData].sort(
        (a, b) => Number(b.isActive) - Number(a.isActive),
      );
      setCardItems(sortedData);
      setFilteredItems(sortedData);
    }
  }, [cardData]);

  useEffect(() => {
    const sortedData = [...filteredItems].sort(
      (a, b) => Number(b.isActive) - Number(a.isActive),
    );
    const newlyActiveCard = sortedData.find((item) => item.isActive);
    if (newlyActiveCard) {
      setExpandedId(newlyActiveCard.scenarioId);
    }
  }, [filteredItems]);

  if (loading) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        Loading Service Scenarios...
      </div>
    );
  }

  return (
    <>
      <div className="search-bar-wrapper">
        <div className="row">
          <div className="col-md-3 mb-3">
            <div className="input-section">
              <Controller
                name="searchText"
                control={control}
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Input
                    className="custom-input-width"
                    type="text"
                    variant="default"
                    id="textInput"
                    label="Search by Scenario Names"
                    fit="small"
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e);
                      handleSearch(e.target.value);
                    }}
                    placeholder="search scenario name..."
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card-list-wrapper">
        <div className="row">
          {paginatedItems.map((item, index) => (
            <div key={`${item.scenarioId}-${index}`} className="col-md-12">
              <div
                className={`border border-gray-300 transition-all mb-3 overflow-hidden ${
                  expandedId === item.scenarioId
                    ? "bg-white shadow-sm"
                    : "bg-gray-100"
                }`}
                style={{
                  padding: "7px",
                  minHeight: "10px",
                  fontSize: "1.05rem",
                }}
              >
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <button
                      style={{
                        fontSize: "16px", // text-base (16px)
                        fontWeight: 500, // font-medium (500 weight)
                        width: "100%", // w-full
                        textAlign: "start", // text-start
                        whiteSpace: "nowrap", // truncate (with overflow settings below)
                        overflow: "hidden", // truncate
                        textOverflow: "ellipsis", // truncate
                        backgroundColor: "transparent", // bg-transparent
                        border: "0", // border-0
                        padding: "0", // p-0
                        margin: "0", // m-0
                      }}
                      onClick={() => toggleCard(item.scenarioId)}
                    >
                      {item.scenarioName}
                      <span
                        className="ml-2 text-gray-500 text-lg"
                        style={{ marginLeft: "8px" }}
                      >
                        {expandedId === item.scenarioId ? (
                          <span id="arrow">&#8743;</span>
                        ) : (
                          <span id="down-arrow"> &#8744;</span>
                        )}
                      </span>
                    </button>
                  </div>

                  <div className="col-md-8 d-flex justify-content-end gap-2">
                    {/* <span
                      className={`status-badge ${
                        item.isActive ? "status-active" : "status-inactive"
                      }`}
                    > */}
                    {item.isActive ? (
                      <div style={{ marginTop: "4px" }}>
                        <Tag
                          label="Active"
                          variant="success"
                          className="small-tag"
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: "4px" }}>
                        <Tag
                          label="InActive"
                          variant="default"
                          className="small-tag"
                        />
                      </div>
                    )}

                    {userRole === appRoles.DeploymentUser && (
                      <>
                        <Button
                          appearance="default"
                          className="small-button"
                          id="primary"
                          icon="pencil"
                          name="Edit"
                          fit="small"
                          iconPosition="right"
                          variant="outlined"
                          onClick={() => onEditClick?.(item)}
                        />
                        <Button
                          appearance="alt"
                          id="btnstatus"
                          label={item.isActive ? "Deactivate" : "Activate"}
                          name={item.isActive ? "deactivate" : "activate"}
                          fit="small"
                          variant="primary"
                          onClick={() => {
                            setTargetCardId(item.scenarioId);
                            setShowConfirm(true);
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>

                {expandedId === item.scenarioId &&
                  item.conditions &&
                  item.conditions.length > 0 && (
                    <div className="mt-2 text-sm">
                      {item.conditions.map((condition, index) => (
                        <div key={index} className="mb-3 text-gray-600">
                          <div
                            className="font-medium text-gray-800"
                            style={{ fontSize: "14px" }}
                          >
                            {condition.cargoFlowDirectionName}
                          </div>

                          <div className="text-xs">
                            <span
                              style={{
                                color: "#545454",
                                fontSize: "13px",
                                fontWeight: "bold",
                                marginTop: "50px",
                              }}
                            >
                              Utilization period: Between{" "}
                            </span>
                            <span
                              className="font-semibold text-gray-800"
                              style={{ fontSize: "13px" }}
                            >
                              {condition.minPercentage} % and
                            </span>{" "}
                            <span
                              className="font-semibold text-gray-800"
                              style={{ fontSize: "13px" }}
                            >
                              {condition.maxPercentage}%
                            </span>
                            <span
                              style={{
                                color: "#545454",
                                fontSize: "13px",
                                fontWeight: "bold",
                                marginTop: "50px",
                                marginLeft: "20px",
                              }}
                            >
                              Period: past:
                            </span>{" "}
                            <span
                              className="font-semibold text-gray-800"
                              style={{ fontSize: "13px" }}
                            >
                              {condition.period} months
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          ))}

          {showConfirm && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                height: "100vh",
                width: "100vw",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  background: "#fff",
                  padding: "24px",
                  borderRadius: "8px",
                  width: "400px",
                  maxWidth: "90%",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                }}
              >
                {(() => {
                  const card = cardItems.find(
                    (c) => c.scenarioId === targetCardId,
                  );
                  if (!card) return null;
                  return (
                    <>
                      <h5>Confirm Action</h5>
                      <p>
                        Are you sure you want to{" "}
                        <strong>
                          {card.isActive ? "deactivate" : "activate"}
                        </strong>{" "}
                        the scenario <strong>"{card.scenarioName}"</strong>?
                      </p>
                      <div className="d-flex gap-2 justify-content-end mt-3">
                        <Button
                          name="Cancel"
                          label="Cancel"
                          onClick={() => setShowConfirm(false)}
                        />
                        <Button
                          name="Confirm"
                          label="Confirm"
                          onClick={() => {
                            if (targetCardId !== null) {
                              updateScenerioStatus(targetCardId);
                              setExpandedId(targetCardId);
                            }
                            setShowConfirm(false);
                          }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
          <Button
            label="Previous"
            name="Previous"
            disabled={currentPage === 1}
            onClick={handlePrev}
          />

          {[...Array(totalPages)].map((_, index) => {
            const page = index + 1;
            return (
              <Button
                key={page}
                label={String(page)}
                name={`Page ${page}`}
                variant={page === currentPage ? "primary" : "outlined"}
                onClick={() => handlePageClick(page)}
              />
            );
          })}

          <Button
            label="Next"
            name="Next"
            disabled={currentPage === totalPages}
            onClick={handleNext}
          />
        </div>
      )}
    </>
  );
};

export default CardList;
